/**
 * Onboarding storage utilities for tracking tutorial and checklist state
 * Uses localStorage with user-specific keys
 */

const STORAGE_KEY_PREFIX = 'bohol_trabaho_onboarding_';

/**
 * Get the storage key for a specific user
 */
const getStorageKey = (userId) => `${STORAGE_KEY_PREFIX}${userId}`;

/**
 * Default onboarding state
 */
const getDefaultState = () => ({
  tutorialCompleted: false,
  tutorialDismissed: false,
  checklistDismissed: false,
  completedSteps: [],
  firstLoginDate: new Date().toISOString(),
  lastUpdated: new Date().toISOString()
});

/**
 * Get onboarding state for a user
 */
export const getOnboardingState = (userId) => {
  if (!userId) return getDefaultState();

  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (stored) {
      return { ...getDefaultState(), ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error reading onboarding state:', error);
  }

  return getDefaultState();
};

/**
 * Save onboarding state for a user
 */
export const saveOnboardingState = (userId, state) => {
  if (!userId) return;

  try {
    const newState = {
      ...state,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(getStorageKey(userId), JSON.stringify(newState));
  } catch (error) {
    console.error('Error saving onboarding state:', error);
  }
};

/**
 * Mark the tutorial as completed
 */
export const markTutorialCompleted = (userId) => {
  const state = getOnboardingState(userId);
  saveOnboardingState(userId, {
    ...state,
    tutorialCompleted: true
  });
};

/**
 * Dismiss the tutorial (skip without completing)
 */
export const dismissTutorial = (userId) => {
  const state = getOnboardingState(userId);
  saveOnboardingState(userId, {
    ...state,
    tutorialDismissed: true
  });
};

/**
 * Dismiss the checklist widget
 */
export const dismissChecklist = (userId) => {
  const state = getOnboardingState(userId);
  saveOnboardingState(userId, {
    ...state,
    checklistDismissed: true
  });
};

/**
 * Reset tutorial to show it again
 */
export const resetTutorial = (userId) => {
  const state = getOnboardingState(userId);
  saveOnboardingState(userId, {
    ...state,
    tutorialCompleted: false,
    tutorialDismissed: false
  });
};

/**
 * Reset checklist to show it again
 */
export const resetChecklist = (userId) => {
  const state = getOnboardingState(userId);
  saveOnboardingState(userId, {
    ...state,
    checklistDismissed: false
  });
};

/**
 * Mark a checklist step as completed
 */
export const markStepCompleted = (userId, stepId) => {
  const state = getOnboardingState(userId);
  if (!state.completedSteps.includes(stepId)) {
    saveOnboardingState(userId, {
      ...state,
      completedSteps: [...state.completedSteps, stepId]
    });
  }
};

/**
 * Check if a step is completed
 */
export const isStepCompleted = (userId, stepId) => {
  const state = getOnboardingState(userId);
  return state.completedSteps.includes(stepId);
};

/**
 * Get all completed steps
 */
export const getCompletedSteps = (userId) => {
  const state = getOnboardingState(userId);
  return state.completedSteps;
};

/**
 * Check if this is the user's first login (within last 5 minutes of account creation)
 * Uses sessionStorage to track the current session's first view
 */
export const isFirstLogin = (userId) => {
  if (!userId) return false;

  const sessionKey = `bohol_trabaho_first_login_shown_${userId}`;
  let alreadyShown = false;
  try { alreadyShown = sessionStorage.getItem(sessionKey); } catch {}

  if (alreadyShown) return false;

  const state = getOnboardingState(userId);

  // If tutorial already completed or dismissed, not first login
  if (state.tutorialCompleted || state.tutorialDismissed) return false;

  return true;
};

/**
 * Mark that we've shown the first login experience
 */
export const markFirstLoginShown = (userId) => {
  if (!userId) return;
  const sessionKey = `bohol_trabaho_first_login_shown_${userId}`;
  try { sessionStorage.setItem(sessionKey, 'true'); } catch {}
};

/**
 * Clear all onboarding data for a user (for testing)
 */
export const clearOnboardingData = (userId) => {
  if (!userId) return;

  try {
    localStorage.removeItem(getStorageKey(userId));
    sessionStorage.removeItem(`bohol_trabaho_first_login_shown_${userId}`);
  } catch (error) {
    console.error('Error clearing onboarding data:', error);
  }
};
